import type { ReactNode } from 'react';
import { type ExtractRouteName, NavStackContextProvider } from './context';
import type { Route } from './types';
import { useNavStack } from './useNavStack';

interface Props<TRoute extends Route<string>> {
  initialRoutes?: TRoute[];
  children: ReactNode;
}

/**
 * This module provides a simple navigation stack via a provider & context or just a hook.
 * The nav stack can be initialised with an array of routes, or it will default to an empty array.
 * Use the functions to modify the stack and currentRoute to inspect the value. The hook
 * will trigger a re-render when the stack is modified.
 *
 * There are two modes of usage:
 *
 * 1) Use within a single component:
 *
 *     const nav = useNavStack([{name:'list'}]);
 *     const onClick = () => nav.push({name:'details'});
 *     const onBack = () => nav.pop();
 *     return nav.currentRoute.name === 'list' ? <List/> : <Details/>;
 *
 * 2) Use in a component tree:
 *
 *     <MBNavStackProvider initialRoutes={[{name:'list'}]}>{component}</MBNavStackProvider>
 *     ...
 *     const nav = useNavStackContext<YourRouteType>();
 *     ...
 */
export function MBNavStackProvider<TRoute extends Route<string>>({
  initialRoutes,
  children,
}: Props<TRoute>) {
  type RouteName = ExtractRouteName<TRoute>;
  const ctx = useNavStack<RouteName>(initialRoutes as unknown as Route<RouteName>[] | undefined);

  return <NavStackContextProvider value={ctx}>{children}</NavStackContextProvider>;
}
